import { useMemo, useRef, useState } from 'preact/hooks';
import type { AuthMode } from '@/types';
import { useCheckoutPresentation } from '@/components/CheckoutPresentation';
import { getIdentityAdapter } from '@/identityAdapter';
import { ensureEzAuthFacade } from '@/ensureEzAuth';
import { logError } from '@/sdkConsole';
import { t, useT } from '@/i18n/t';

interface Props {
  // The domain's auth mode + id. ezAuth renders the in-page password form and
  // adopts the resulting session; BYO navigates away to the publisher's own
  // goToCreateAccount().
  authMode?: AuthMode;
  domainId?: number;
  // The masked form of the email the visitor checked out as a guest with, shown
  // for context. The plaintext never reaches the page.
  maskedEmail: string;
  // The signed, short-lived attestation (from the checkout completion) that sol
  // verifies to source the email at register. Opaque to the page.
  emailAttestation: string;
  // Dismisses the modal, or skips the inline account offer when no successUrl exists.
  onContinue: () => void;
}

// 'cta' is the collapsed prompt; 'register' collects the password; 'done'
// confirms a created account (ezAuth only — BYO navigates away).
type View = 'cta' | 'register' | 'done';

// AccountConversion offers a guest buyer an optional, skippable "create an
// account" step on the success screen, so their next return is a real login
// (password/Google) instead of a one-time magic link. Entitlements are
// email-keyed, so an account made with the same email inherits the purchase with
// no backend linking. The guest's plaintext email never reaches the page: the
// form collects only a password, and sol sources the email from the email
// attestation the widget presents. BYO forwards to the publisher's own
// goToCreateAccount() (SM does not own that page).
export function AccountConversion({ authMode, domainId, maskedEmail, emailAttestation, onContinue }: Props) {
  useT();
  const { inline, successUrl } = useCheckoutPresentation();
  const adapter = useMemo(() => getIdentityAdapter(authMode, domainId), [authMode, domainId]);
  const [view, setView] = useState<View>('cta');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Guards against a double-submit while a register is in flight.
  const inFlight = useRef(false);

  const handleCreateAccount = () => {
    // ezAuth collects a password in-page and registers via sol; BYO navigates
    // away to the publisher's registration (nothing to await — the visitor
    // returns via the publisher flow, and authChanged re-establishes their
    // session).
    if (adapter.capabilities.loginInPage) {
      setView('register');
      return;
    }
    adapter.promptRegister();
  };

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    if (inFlight.current) {
      return;
    }
    setError(null);
    if (password.length < 8) {
      setError(t('accountConversion.passwordTooShort'));
      return;
    }
    if (password !== confirm) {
      setError(t('accountConversion.passwordMismatch'));
      return;
    }
    if (typeof domainId !== 'number') {
      setError(t('accountConversion.failed'));
      return;
    }
    inFlight.current = true;
    setSubmitting(true);
    try {
      // The ezAuth SDK owns the register call: it posts the password +
      // attestation to sol and adopts the issued session, so subsequent loads
      // are ezAuth-authenticated. Sol sources the email from the attestation —
      // the plaintext never reaches the page. The facade can be a reused,
      // already-initialized publisher install on an older bundle without this
      // method — surface the generic error rather than a raw TypeError.
      const facade = await ensureEzAuthFacade(domainId);
      if (!facade || typeof facade.registerWithEmailToken !== 'function') {
        throw new Error('ezAuth registerWithEmailToken unavailable');
      }
      // No skipVerificationEmail: SM's ezAuth trust gate refuses an unverified
      // account on a domain where the email has data, and a converted guest
      // always has data here (they just bought). The verification email is
      // the only prompt that lifts the refusal, so it must be sent.
      await facade.registerWithEmailToken(emailAttestation, password);
      setView('done');
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (code === 'account_exists') {
        setError(t('accountConversion.accountExists'));
      } else if (code === 'password_compromised') {
        setError(t('accountConversion.passwordCompromised'));
      } else {
        logError('account conversion register failed', err);
        setError(t('accountConversion.failed'));
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  const skipAction = inline && successUrl
    ? <a class="sm-secondary-link" href={successUrl}>{t('accountConversion.noThanks')}</a>
    : <button class="sm-secondary-link" type="button" onClick={onContinue}>{t('accountConversion.noThanks')}</button>;

  if (view === 'done') {
    return (
      <div class="sm-conversion">
        <p class="sm-identity-note" role="status">
          <strong>{t('accountConversion.created')}</strong> {t('accountConversion.createdDetail')}
        </p>
        {inline && successUrl && <a class="sm-btn sm-btn-primary" href={successUrl}>{t('cta.continue')}</a>}
        {!inline && <button class="sm-btn sm-btn-primary" type="button" onClick={onContinue}>
          {t('cta.continue')}
        </button>}
      </div>
    );
  }

  if (view === 'register') {
    return (
      <div class="sm-conversion">
        <p class="sm-identity-note">
          {t('accountConversion.promptBefore')} <strong>{maskedEmail}</strong> {t('accountConversion.promptAfter')}
        </p>
        <form onSubmit={handleSubmit} class="sm-form">
          <label class="sm-field">
            <span>{t('emailField.label')}</span>
            <input
              type="text"
              class="sm-input"
              value={maskedEmail}
              disabled
              aria-label={t('emailField.label')}
            />
          </label>
          <label class="sm-field">
            <span>{t('accountConversion.passwordLabel')}</span>
            <input
              type="password"
              class="sm-input"
              autocomplete="new-password"
              value={password}
              onInput={(e) => setPassword((e.target as HTMLInputElement).value)}
              required
              aria-label={t('accountConversion.passwordLabel')}
            />
          </label>
          <label class="sm-field">
            <span>{t('accountConversion.confirmLabel')}</span>
            <input
              type="password"
              class="sm-input"
              autocomplete="new-password"
              value={confirm}
              onInput={(e) => setConfirm((e.target as HTMLInputElement).value)}
              required
              aria-label={t('accountConversion.confirmLabel')}
            />
          </label>
          {error && (
            <p class="sm-error" role="alert">
              {error}
            </p>
          )}
          <button class="sm-btn sm-btn-primary" type="submit" disabled={submitting}>
            {submitting ? t('accountConversion.creating') : t('accountConversion.createAccount')}
          </button>
        </form>
        {skipAction}
      </div>
    );
  }

  return (
    <div class="sm-conversion">
      <p class="sm-conversion-prompt">
        {t('accountConversion.promptBefore')} <strong>{maskedEmail}</strong> {t('accountConversion.promptAfter')}
      </p>
      <button class="sm-btn sm-btn-primary" type="button" onClick={handleCreateAccount}>
        {t('accountConversion.createAccount')}
      </button>
      {skipAction}
    </div>
  );
}
